import { jwtDecode } from 'jwt-decode';
import type { User } from '@/core/user/user.model';
import type { Permission } from '@/core/user/permission.model';
import { isPermission } from '@/core/user/permission.model';

interface CognitoIdTokenClaims {
  firstName?: string;
  lastName?: string;
  email?: string;
}

interface CognitoAccessTokenClaims {
  permissions?: unknown;
}

interface ExpirableClaims {
  exp?: number;
}

export function isTokenExpired(token: string): boolean {
  try {
    const { exp } = jwtDecode<ExpirableClaims>(token);
    if (exp === undefined) return true;
    return exp * 1000 <= Date.now();
  } catch {
    return true;
  }
}

function decodeIdTokenClaims(idToken: string): CognitoIdTokenClaims | undefined {
  try {
    return jwtDecode<CognitoIdTokenClaims>(idToken);
  } catch {
    return undefined;
  }
}

function decodeAccessTokenClaims(accessToken: string): CognitoAccessTokenClaims | undefined {
  try {
    return jwtDecode<CognitoAccessTokenClaims>(accessToken);
  } catch {
    return undefined;
  }
}

function permissionsFromAccessToken(accessToken: string): Permission[] {
  const claims = decodeAccessTokenClaims(accessToken);
  const rawPermissions = claims?.permissions;
  if (!Array.isArray(rawPermissions)) return [];

  return rawPermissions.filter((value): value is string => typeof value === 'string').filter(isPermission);
}

export function userFromTokens(idToken: string, accessToken: string): User | undefined {
  const claims = decodeIdTokenClaims(idToken);
  if (!claims) return undefined;

  return {
    username: `${claims.firstName ?? ''} ${claims.lastName ?? ''}`.trim(),
    email: claims.email ?? '',
    permissions: permissionsFromAccessToken(accessToken),
  };
}
